import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import moment from 'moment'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import SearchableSelect from 'components/SearchableSelect'
import ReportPeriodPicker, { resolvePeriod } from 'components/ReportPeriodPicker'
import TaskOriginPicker from 'features/taskManagement/components/TaskOriginPicker'
import { originLabel } from 'features/taskManagement/taskOrigin'
import { fetchDashboardScopeOptions } from 'services/BpService'
import { exportKpiReportPdf } from 'services/reportingService'
import { downloadBlob } from 'utils/downloadBlob'

const ALL_SITES = { type: 'all', id: null, label: 'All Building Passports' }

export const kpiRequestParams = ({ scope, period, origin, assignee } = {}) => {
  const range = resolvePeriod(period)
  return {
    ...(range.from ? { from: range.from, to: range.to } : { period: 'all' }),
    ...(origin ? { origin } : {}),
    ...(scope?.type === 'portfolio' ? { portfolioId: scope.id } : {}),
    ...(scope?.type === 'estate' ? { estateId: scope.id } : {}),
    ...(scope?.type === 'bp' ? { buildingPassportId: scope.id } : {}),
    ...(assignee?.id ? { assigneeId: assignee.id } : {}),
  }
}

export const kpiReportUrl = filters => {
  const p = kpiRequestParams(filters)
  const q = new URLSearchParams()
  if (p.from) { q.set('from', p.from); q.set('to', p.to) }
  if (p.period) q.set('period', p.period)
  if (p.origin) q.set('origin', p.origin)
  if (p.portfolioId) q.set('portfolio_id', p.portfolioId)
  if (p.estateId) q.set('estate_id', p.estateId)
  if (p.buildingPassportId) q.set('building_passport_id', p.buildingPassportId)
  if (p.assigneeId) q.set('assignee_id', p.assigneeId)
  if (filters?.scope?.label && filters.scope.type !== 'all') q.set('scope_label', filters.scope.label)
  if (filters?.assignee?.name) q.set('assignee_name', filters.assignee.name)
  return `/dashboard/kpi-report?${q.toString()}`
}

export const kpiFiltersFromQuery = searchParams => {
  const get = key => searchParams.get(key) || null
  const scopeLabel = get('scope_label')
  let scope = ALL_SITES
  if (get('portfolio_id')) scope = { type: 'portfolio', id: get('portfolio_id'), label: scopeLabel || 'Portfolio' }
  else if (get('estate_id')) scope = { type: 'estate', id: get('estate_id'), label: scopeLabel || 'Estate' }
  else if (get('building_passport_id')) scope = { type: 'bp', id: get('building_passport_id'), label: scopeLabel || 'Building Passport' }

  let period = { preset: 'last_30_days' }
  if (get('from') || get('to')) period = { preset: 'custom', from: get('from') || get('to'), to: get('to') || get('from') }
  else if (get('period') === 'all') period = { preset: 'all' }

  const origin = ['corrective', 'obligation', 'standard'].includes(get('origin')) ? get('origin') : null
  const assignee = get('assignee_id') ? { id: get('assignee_id'), name: get('assignee_name') || 'Selected person' } : null

  return { scope, period, origin, assignee }
}

const slug = value => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')

export const kpiFileName = filters => {
  const range = resolvePeriod(filters?.period)
  const parts = [
    'kpi-report',
    filters?.scope?.type && filters.scope.type !== 'all' ? slug(filters.scope.label) : 'all-buildings',
    range.from ? `${range.from}-to-${range.to}` : 'all-time',
    filters?.origin ? slug(originLabel(filters.origin)) : null,
    filters?.assignee?.name ? slug(filters.assignee.name) : null,
  ].filter(Boolean)
  return `${parts.join('_')}.pdf`
}

export const downloadKpiPdf = async filters => {
  const res = await exportKpiReportPdf(kpiRequestParams(filters))
  downloadBlob(new Blob([res.data], { type: 'application/pdf' }), kpiFileName(filters))
}

const scopeValue = scope => (scope?.type && scope.type !== 'all' ? `${scope.type}:${scope.id}` : 'all')

const KpiReportDialog = ({ open, onClose, initial, primaryAction = 'download', onApply }) => {
  const [filters, setFilters] = useState(() => ({ scope: ALL_SITES, period: { preset: 'last_30_days' }, origin: null, assignee: null, ...initial }))
  const [options, setOptions] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    setFilters({ scope: ALL_SITES, period: { preset: 'last_30_days' }, origin: null, assignee: null, ...initial })
  }, [open])

  useEffect(() => {
    if (!open || options) return
    fetchDashboardScopeOptions()
      .then(res => setOptions(res?.data?.data || res?.data || {}))
      .catch(() => setOptions({}))
  }, [open, options])

  useEffect(() => {
    if (!open) return
    const onKey = e => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const siteOptions = useMemo(() => {
    const list = [{ value: 'all', label: ALL_SITES.label }]
    ;(options?.portfolios || []).forEach(p => list.push({ value: `portfolio:${p.id}`, label: `Portfolio: ${p.name}`, name: p.name }))
    ;(options?.estates || []).forEach(e => list.push({ value: `estate:${e.id}`, label: `Estate: ${e.name}`, name: e.name }))
    ;(options?.buildingPassports || []).forEach(b => list.push({ value: `bp:${b.id}`, label: b.name, name: b.name }))
    const current = scopeValue(filters.scope)
    if (!list.some(o => o.value === current)) list.push({ value: current, label: filters.scope.label, name: filters.scope.label })
    return list
  }, [options, filters.scope])

  if (!open) return null

  const range = resolvePeriod(filters.period)
  const nice = d => moment(d, 'YYYY-MM-DD').format('D MMM YYYY')
  const periodText = !range.from
    ? 'tasks due at any time'
    : range.from === range.to ? `tasks due on ${nice(range.from)}` : `tasks due between ${nice(range.from)} and ${nice(range.to)}`
  const summary = [
    filters.scope?.type === 'all' ? 'all your Building Passports' : filters.scope.label,
    periodText,
    filters.origin ? `${originLabel(filters.origin).toLowerCase()} tasks only` : 'all task types',
    filters.assignee ? `assigned to ${filters.assignee.name}` : null,
  ].filter(Boolean).join(', ')

  const chooseSite = value => {
    if (!value || value === 'all') return setFilters(f => ({ ...f, scope: ALL_SITES }))
    const [type, id] = String(value).split(':')
    const opt = siteOptions.find(o => o.value === value)
    setFilters(f => ({ ...f, scope: { type, id, label: opt?.name || opt?.label || 'Selected site' } }))
  }

  const download = async () => {
    if (busy) return
    setBusy(true)
    try {
      await downloadKpiPdf(filters)
      toast.success('KPI report downloaded.')
      onClose()
    } catch (e) {
      toast.error('Could not create the KPI report. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const openReport = () => {
    if (onApply) {
      onApply(filters)
      onClose()
      return
    }
    window.open(kpiReportUrl(filters), '_blank')
    onClose()
  }

  return createPortal(
    <Backdrop onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
      <Dialog role='dialog' aria-modal='true' aria-labelledby='kpi-dialog-title'>
        <Head>
          <div>
            <Title id='kpi-dialog-title'>{primaryAction === 'apply' ? 'Change what the KPI report covers' : 'Download KPI report'}</Title>
            <Sub>It starts with the filters you are looking at. Change anything you need, then download.</Sub>
          </div>
          <CloseBtn type='button' aria-label='Close' onClick={onClose}><CloseRoundedIcon /></CloseBtn>
        </Head>

        <Body>
          <Field>
            <Label htmlFor='kpi-sites'>Sites</Label>
            <SearchableSelect
              id='kpi-sites'
              value={scopeValue(filters.scope)}
              onChange={chooseSite}
              options={siteOptions}
              placeholder='All Building Passports'
              searchPlaceholder='Search portfolios, estates and buildings…'
              ariaLabel='Which sites the report covers'
            />
          </Field>

          <Field>
            <Label as='span'>Dates</Label>
            <Hint>Tasks are counted by their due date. Remedials are counted by the date they were raised or resolved. Pick a quick period or exact dates.</Hint>
            <ReportPeriodPicker value={filters.period} onChange={period => setFilters(f => ({ ...f, period }))} label='Tasks due' />
          </Field>

          <Field>
            <Label as='span'>Task type</Label>
            <TaskOriginPicker value={filters.origin} onChange={origin => setFilters(f => ({ ...f, origin }))} />
          </Field>

          {filters.assignee && (
            <Field>
              <Label as='span'>Person</Label>
              <PersonChip>
                Only tasks assigned to <strong>{filters.assignee.name}</strong>
                <RemoveBtn type='button' onClick={() => setFilters(f => ({ ...f, assignee: null }))} aria-label={`Include everyone, not only ${filters.assignee.name}`}>
                  Include everyone
                </RemoveBtn>
              </PersonChip>
            </Field>
          )}

          <Summary>
            <strong>This report will cover:</strong> {summary}. It also shows remedial performance for the same sites and dates; the task type and person filters apply to tasks only.
          </Summary>
        </Body>

        <Foot>
          <SecondaryBtn type='button' onClick={openReport}>
            <OpenInNewRoundedIcon style={{ fontSize: 17 }} />
            {primaryAction === 'apply' ? 'Show report' : 'Open report on screen'}
          </SecondaryBtn>
          <PrimaryBtn type='button' onClick={download} disabled={busy}>
            <FileDownloadRoundedIcon style={{ fontSize: 17 }} />
            {busy ? 'Preparing PDF…' : 'Download PDF'}
          </PrimaryBtn>
        </Foot>
      </Dialog>
    </Backdrop>,
    document.body
  )
}

export default KpiReportDialog

const Backdrop = styled.div`
  position: fixed; inset: 0; z-index: 100000;
  background: rgba(16, 24, 40, 0.45);
  display: flex; align-items: center; justify-content: center; padding: 16px;

  @media (max-width: 768px) { align-items: flex-end; padding: 0; }
`

const Dialog = styled.div`
  width: 100%; max-width: 760px; max-height: calc(100vh - 48px);
  display: flex; flex-direction: column;
  background: #fff; border-radius: 14px;
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.18);

  @media (max-width: 768px) { max-width: none; max-height: 92vh; border-radius: 14px 14px 0 0; }
`

const Head = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
  padding: 20px 24px 14px; border-bottom: 1px solid #eef0f4;

  @media (max-width: 768px) { padding: 18px 20px 12px; }
`

const Title = styled.h2` margin: 0 0 4px; font-size: 18px; font-weight: 800; color: #101828; `
const Sub = styled.p` margin: 0; font-size: 14px; line-height: 1.45; color: #475467; `

const CloseBtn = styled.button`
  flex-shrink: 0; width: 40px; height: 40px; border-radius: 10px; border: none; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  background: transparent; color: #475467;
  &:hover { background: #f2f4f7; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`

const Body = styled.div`
  padding: 20px 24px; overflow-y: auto;
  display: flex; flex-direction: column; gap: 20px;

  @media (max-width: 768px) { padding: 16px 20px; gap: 18px; }
`

const Field = styled.div` display: flex; flex-direction: column; gap: 6px; min-width: 0; `
const Label = styled.label` font-size: 14px; font-weight: 700; color: #101828; `
const Hint = styled.span` font-size: 13px; color: #667085; margin-top: -2px; `

const PersonChip = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: 8px 12px;
  padding: 10px 12px; border-radius: 10px; background: #f6f7fb; border: 1px solid #e4e7ec;
  font-size: 14px; color: #344054;
`

const RemoveBtn = styled.button`
  font: inherit; font-size: 13.5px; font-weight: 700; cursor: pointer;
  min-height: 36px; padding: 6px 12px; border-radius: 8px;
  border: 1px solid #d0d5dd; background: #fff; color: #344054;
  &:hover { border-color: #33becc; color: #1d7f89; }
`

const Summary = styled.p`
  margin: 0; padding: 12px 14px; border-radius: 10px;
  background: #e6f7f9; color: #1d5f66; font-size: 14px; line-height: 1.5;
  strong { color: #134e54; }
`

const Foot = styled.div`
  display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 10px;
  padding: 16px 24px 20px; border-top: 1px solid #eef0f4;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    button { width: 100%; justify-content: center; }
  }
`

const baseBtn = `
  font: inherit; display: inline-flex; align-items: center; gap: 8px;
  min-height: 44px; padding: 10px 16px; border-radius: 10px; cursor: pointer;
  font-size: 14.5px; font-weight: 700;
  &:focus-visible { outline: 2px solid #1d7f89; outline-offset: 2px; }
  &:disabled { opacity: 0.6; cursor: default; }
`

const PrimaryBtn = styled.button`
  ${baseBtn}
  border: 1px solid #33becc; background: #33becc; color: #fff;
  &:hover:not(:disabled) { background: #2aa6b3; }
`

const SecondaryBtn = styled.button`
  ${baseBtn}
  border: 1px solid #d0d5dd; background: #fff; color: #344054;
  &:hover { border-color: #33becc; }
`
